import { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, MessageSquare, Filter, ArrowRightLeft, Users, Trash2, Sparkles, ShoppingCart, Package, Mail, BarChart3 } from 'lucide-react';
import { InventoryClassBadge } from './InventoryClassBadge';
import { InventoryDetail } from './InventoryDetail';
import { InlineCommentInput } from './InlineCommentInput';
import { ContextMenu } from '../ui/ContextMenu';
import type { ContextMenuItem } from '../ui/ContextMenu';
import type { InventoryItem, InventoryFilters } from '../../types/inventory';

export interface FilterByValueAction {
    filterKey: keyof InventoryFilters;
    value: string;
    label: string;
}

interface InventoryRowProps {
    item: InventoryItem;
    isExpanded: boolean;
    isSelected?: boolean;
    onToggle: () => void;
    canEdit: boolean;
    onCommentAdded: () => void;
    colCount: number;
    onFilterByValue?: (action: FilterByValueAction) => void;
    onShowTransactions?: (item: InventoryItem) => void;
    onToggleFlag?: (item: InventoryItem, flagType: 'ToBeDestroyed' | 'NewProduct') => void;
    onShowBuyers?: (item: InventoryItem) => void;
    onSendEmail?: (item: InventoryItem) => void;
    onShowEmails?: (item: InventoryItem) => void;
    onShowDetails?: (itemId: string) => void;
    compact?: boolean;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatNumber(num: number | null | undefined): string {
    if (num == null) return '-';
    return Math.round(num).toLocaleString('de-CH');
}

function formatEUR(num: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
}

function formatPricePerKg(num: number | null | undefined): string {
    if (num == null) return '—';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

function daysAgoShort(days: number | null): string {
    if (days == null) return '';
    if (days === 0) return 'today';
    return `${days}d`;
}

export function InventoryRow({ item, isExpanded, isSelected = false, onToggle, canEdit, onCommentAdded, colCount, onFilterByValue, onShowTransactions, onToggleFlag, onShowBuyers, onSendEmail, onShowEmails, onShowDetails, compact = false }: InventoryRowProps) {
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);
    /** Busiest Betreuer first (ordered by the API); empty for articles never sold to anyone. */
    const managers = item.SalesManagers ?? [];

    const handleContextMenu = useCallback((e: React.MouseEvent, filterAction?: FilterByValueAction) => {
        e.preventDefault();
        e.stopPropagation();

        const menuItems: ContextMenuItem[] = [];

        // Show Details (graphical drawer) — top entry
        if (onShowDetails) {
            menuItems.push({
                label: 'Show Details',
                icon: <BarChart3 className="h-3.5 w-3.5" />,
                onClick: () => onShowDetails(item.ItemID),
            });
        }

        // Filter option (only for filterable cells)
        if (filterAction && onFilterByValue) {
            menuItems.push({
                label: `Filter by "${filterAction.label}"`,
                icon: <Filter className="h-3.5 w-3.5" />,
                onClick: () => onFilterByValue(filterAction),
            });
        }

        // Show Transactions (always available)
        if (onShowTransactions) {
            menuItems.push({
                label: 'Show Transactions',
                icon: <ArrowRightLeft className="h-3.5 w-3.5" />,
                onClick: () => onShowTransactions(item),
            });
        }

        // Show Buyers
        if (onShowBuyers) {
            menuItems.push({
                label: 'Show Buyers',
                icon: <Users className="h-3.5 w-3.5" />,
                onClick: () => onShowBuyers(item),
            });
        }

        // Send Email (edit permission only)
        if (onSendEmail && canEdit) {
            menuItems.push({
                label: 'Send Email about this item',
                icon: <Mail className="h-3.5 w-3.5" />,
                onClick: () => onSendEmail(item),
            });
        }

        if (menuItems.length > 0) {
            setContextMenu({ x: e.clientX, y: e.clientY, items: menuItems });
        }
    }, [onShowDetails, onFilterByValue, onShowTransactions, onShowBuyers, onSendEmail, canEdit, item]);

    const handleRowClick = () => {
        // In compact/drawer mode, row-click switches the drawer article instead of toggling expand.
        if (compact && onShowDetails) {
            onShowDetails(item.ItemID);
        } else {
            onToggle();
        }
    };

    const classLabel = item.ClassName || `Class ${item.InventoryClass}`;

    return (
        <>
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenu.items}
                    onClose={() => setContextMenu(null)}
                />
            )}
            <tr
                className={`cursor-pointer text-sm hover:bg-gray-50 ${isSelected ? 'bg-blue-100/60' : isExpanded ? 'bg-blue-50/50' : ''}`}
                onClick={handleRowClick}
            >
                <td className="px-3 py-2">
                    {!compact && (isExpanded
                        ? <ChevronDown className="h-4 w-4 text-gray-400" />
                        : <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                </td>
                {!compact && (
                    <td
                        className="px-3 py-2 font-medium text-gray-700"
                        onContextMenu={e => handleContextMenu(e, { filterKey: 'companyCode', value: item.CompanyCode, label: item.CompanyCode })}
                    >{item.CompanyCode}</td>
                )}
                <td
                    className="max-w-[160px] truncate px-3 py-2"
                    onContextMenu={e => handleContextMenu(e, { filterKey: 'search', value: item.ProductName, label: item.ProductName })}
                >
                    <div className="text-gray-900">{item.ProductName}</div>
                    <div className="text-xs text-gray-400">{item.ProductCode}</div>
                </td>
                {!compact && (
                    <td
                        className="max-w-[120px] truncate px-3 py-2 text-gray-700"
                        onContextMenu={e => handleContextMenu(e, { filterKey: 'supplierCode', value: item.SupplierCode, label: item.SupplierName })}
                    >{item.SupplierName}</td>
                )}
                {!compact && (
                    <td
                        className="px-3 py-2 text-gray-600 whitespace-nowrap"
                        onContextMenu={e => handleContextMenu(e, { filterKey: 'warehouseCode', value: `${item.CompanyCode}|${item.WarehouseCode}`, label: item.WarehouseName + (item.WarehouseCountry ? ` (${item.WarehouseCountry})` : '') })}
                    >
                        {item.WarehouseName}
                        {item.WarehouseCountry && <span className="ml-1 text-xs text-gray-400">({item.WarehouseCountry})</span>}
                    </td>
                )}
                <td
                    className="px-3 py-2"
                    onContextMenu={e => handleContextMenu(e, { filterKey: 'inventoryClass', value: String(item.InventoryClass), label: classLabel })}
                >
                    <InventoryClassBadge
                        inventoryClass={item.InventoryClass}
                        stockQuantity={item.StockQuantity}
                        rolling12MSalesQty={item.Rolling12MSalesQty}
                        stockCoverageMonths={item.StockCoverageMonths}
                    />
                </td>
                <td className="px-3 py-2 text-right font-mono text-gray-700" onContextMenu={e => handleContextMenu(e)}>{formatNumber(item.StockQuantity)}</td>
                {!compact && (
                <td className="px-3 py-2 text-right font-mono text-gray-900 font-medium" onContextMenu={e => handleContextMenu(e)}>{formatEUR(item.StockValueEUR)}</td>
                )}
                {!compact && (
                <td className="px-3 py-2 text-right font-mono text-gray-600" onContextMenu={e => handleContextMenu(e)}>{formatPricePerKg(item.PricePerKgEUR)}</td>
                )}
                {!compact && (
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap" onContextMenu={e => handleContextMenu(e)}>{item.OldestBatchDate ? formatDate(item.OldestBatchDate) : '—'}</td>
                )}
                {!compact && (
                    <td
                        className="px-3 py-2 text-gray-600 whitespace-nowrap"
                        onContextMenu={e => handleContextMenu(e)}
                        title={item.LatestSalesDate ? [
                            item.LatestSalesDocumentNr,
                            item.LatestSalesQuantity != null ? `Qty: ${formatNumber(item.LatestSalesQuantity)}` : null,
                            item.LatestSalesPriceEUR != null ? `EUR ${item.LatestSalesPriceEUR.toFixed(2)}` : null,
                            item.LatestSalesCustomerName,
                            item.LatestSalesManager,
                        ].filter(Boolean).join(' | ') : undefined}
                    >
                        {formatDate(item.LatestSalesDate)}
                        {item.DaysSinceLastSale != null && (
                            <span className="ml-1 text-xs text-gray-400">{daysAgoShort(item.DaysSinceLastSale)}</span>
                        )}
                    </td>
                )}
                {!compact && (
                    <td
                        className="px-3 py-2 text-gray-600 whitespace-nowrap"
                        onContextMenu={e => handleContextMenu(e)}
                        title={item.LatestPurchaseDate ? [
                            item.LatestPurchaseDocumentNr,
                            item.LatestPurchaseQuantity != null ? `Qty: ${formatNumber(item.LatestPurchaseQuantity)}` : null,
                            item.LatestPurchaseCostLCY != null ? `Cost: ${item.LatestPurchaseCostLCY.toFixed(2)}` : null,
                        ].filter(Boolean).join(' | ') : undefined}
                    >
                        {formatDate(item.LatestTransactionDate)}
                        {item.DaysSinceLastTransaction != null && (
                            <span className="ml-1 text-xs text-gray-400">{daysAgoShort(item.DaysSinceLastTransaction)}</span>
                        )}
                    </td>
                )}
                {/* Who is behind the article. The point of the whole feature: this used to
                    require opening each product one by one. Clicking opens the buyers modal. */}
                {!compact && (
                    <td className="px-3 py-2 whitespace-nowrap" onContextMenu={e => handleContextMenu(e)}>
                        {managers.length === 0 ? (
                            <span className="text-xs text-gray-300" title="Never sold to any customer">—</span>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onShowBuyers?.(item)}
                                disabled={!onShowBuyers}
                                title={managers
                                    .map(m => `${m.salesManagerName}: ${m.customerCount} customer${m.customerCount > 1 ? 's' : ''}`
                                        + (m.lastSaleDate ? ` (last ${formatDate(m.lastSaleDate)})` : ''))
                                    .join('\n')}
                                className="flex items-center gap-1.5 text-xs text-gray-600 enabled:hover:text-blue-700 disabled:cursor-default"
                            >
                                <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700">
                                    <Users className="h-3 w-3" />
                                    {item.ScopeCustomerCount ?? 0}
                                </span>
                                <span className="max-w-[10rem] truncate">
                                    {managers[0].salesManagerName}
                                    {managers.length > 1 && (
                                        <span className="text-gray-400"> +{managers.length - 1}</span>
                                    )}
                                </span>
                            </button>
                        )}
                    </td>
                )}
                {!compact && (
                    <td className="px-3 py-2 whitespace-nowrap">
                        {(item.OpenSalesOrderCount > 0 || item.OpenPurchaseOrderCount > 0) && (
                            <div className="flex items-center gap-1.5 text-xs">
                                {item.OpenSalesOrderCount > 0 && (
                                    <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700" title={`${item.OpenSalesOrderCount} Open Sales Order${item.OpenSalesOrderCount > 1 ? 's' : ''}`}>
                                        <ShoppingCart className="h-3 w-3" />
                                        {item.OpenSalesOrderCount}
                                    </span>
                                )}
                                {item.OpenPurchaseOrderCount > 0 && (
                                    <span className="inline-flex items-center gap-0.5 rounded bg-sky-50 px-1.5 py-0.5 text-sky-700" title={`${item.OpenPurchaseOrderCount} Open Purchase Order${item.OpenPurchaseOrderCount > 1 ? 's' : ''}`}>
                                        <Package className="h-3 w-3" />
                                        {item.OpenPurchaseOrderCount}
                                    </span>
                                )}
                            </div>
                        )}
                    </td>
                )}
                <td className="relative px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                        {/* Details button (opens drawer) */}
                        {onShowDetails && (
                            <button
                                onClick={e => { e.stopPropagation(); onShowDetails(item.ItemID); }}
                                className="rounded p-0.5 text-gray-400 transition-colors hover:text-blue-600"
                                title="Show article details"
                            >
                                <BarChart3 className="h-3.5 w-3.5" />
                            </button>
                        )}
                        {/* Flag icons — hidden in compact mode to save space */}
                        {!compact && canEdit ? (
                            <>
                                <button
                                    onClick={e => { e.stopPropagation(); onToggleFlag?.(item, 'ToBeDestroyed'); }}
                                    className={`rounded p-0.5 transition-colors ${item.IsToBeDestroyed ? 'text-red-600 hover:text-red-800' : 'text-gray-300 hover:text-red-400'}`}
                                    title={item.IsToBeDestroyed ? 'Remove "To Be Destroyed"' : 'Mark "To Be Destroyed"'}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={e => { e.stopPropagation(); onToggleFlag?.(item, 'NewProduct'); }}
                                    className={`rounded p-0.5 transition-colors ${item.IsNewProduct ? 'text-blue-600 hover:text-blue-800' : 'text-gray-300 hover:text-blue-400'}`}
                                    title={item.IsNewProduct ? 'Remove "New Product"' : 'Mark "New Product"'}
                                >
                                    <Sparkles className="h-3.5 w-3.5" />
                                </button>
                            </>
                        ) : !compact && (
                            <>
                                {item.IsToBeDestroyed && <span title="To Be Destroyed"><Trash2 className="h-3.5 w-3.5 text-red-600" /></span>}
                                {item.IsNewProduct && <span title="New Product"><Sparkles className="h-3.5 w-3.5 text-blue-600" /></span>}
                            </>
                        )}
                        {!compact && item.EmailCount > 0 && (
                            <button
                                onClick={e => { e.stopPropagation(); onShowEmails?.(item); }}
                                className="inline-flex items-center gap-0.5 rounded p-0.5 text-xs text-emerald-600 transition-colors hover:text-emerald-800"
                                title={`${item.EmailCount} email${item.EmailCount > 1 ? 's' : ''} sent — view details`}
                            >
                                <Mail className="h-3.5 w-3.5" />
                                {item.EmailCount > 1 && item.EmailCount}
                            </button>
                        )}
                        {!compact && item.CommentCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                <MessageSquare className="h-3.5 w-3.5" />
                                {item.CommentCount}
                            </span>
                        )}
                        {!compact && canEdit && (
                            <InlineCommentInput
                                itemId={item.ItemID}
                                onCommentAdded={onCommentAdded}
                            />
                        )}
                    </div>
                </td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan={colCount} className="border-b border-blue-100 bg-blue-50/30 px-4 py-4">
                        <InventoryDetail
                            itemId={item.ItemID}
                            canEdit={canEdit}
                            onCommentAdded={onCommentAdded}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}
